// /opt/isp_erp/frontend/src/pages/warehouse/RMA.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Build,
  Delete,
  Visibility,
  Description,
  Refresh,
  CheckCircle,
  Close,
  AssignmentReturn,
  Dangerous as DangerousIcon  // ✅ NUEVO: ícono para baja definitiva
} from '@mui/icons-material';
import { warehouseAPI } from '../../api/warehouse.api';
import ActasRMA from '../../components/warehouse/ActasRMA';

const RMA = () => {
  const [equiposRMA, setEquiposRMA] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEquipo, setSelectedEquipo] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [reingresoOpen, setReingresoOpen] = useState(false);
  const [bajaOpen, setBajaOpen] = useState(false);  // ✅ NUEVO
  const [tabValue, setTabValue] = useState(0);
  const [reingresoData, setReingresoData] = useState({
    observaciones: '',
    reparado: true
  });
  // ✅ NUEVO: Estado para baja definitiva
  const [bajaData, setBajaData] = useState({
    motivo: '',
    confirmacion: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    cargarEquiposRMA();
  }, []);

  const cargarEquiposRMA = async () => {
    setLoading(true);
    try {
      // Trae solo los equipos RMA_BAJA desde el servidor, de 200 en 200
      const todos = [];
      const tamano = 200;
      const maxPaginas = 25; // tope de seguridad: 5.000 equipos
      for (let pagina = 1; pagina <= maxPaginas; pagina++) {
        const response = await warehouseAPI.listarEquiposPaginado({
          estado: 'RMA_BAJA',
          page: pagina,
          page_size: tamano
        });
        todos.push(...(response.data.items || []));
        if (pagina >= (response.data.pages || 0)) break;
      }
      setEquiposRMA(todos);
    } catch (error) {
      console.error('Error cargando equipos RMA:', error);
    } finally {
      setLoading(false);
    }
  };

  const verDetalle = (equipo) => {
    setSelectedEquipo(equipo);
    setDetailOpen(true);
  };

  const abrirReingreso = (equipo) => {
    setSelectedEquipo(equipo);
    setReingresoData({
      observaciones: '',
      reparado: true
    });
    setReingresoOpen(true);
  };

  // ✅ NUEVO: Abrir diálogo de baja definitiva
  const abrirBaja = (equipo) => {
    setSelectedEquipo(equipo);
    setBajaData({
      motivo: '',
      confirmacion: false
    });
    setBajaOpen(true);
  };

  const registrarReingreso = async () => {
    setSubmitting(true);
    try {
      await warehouseAPI.reingresarEquipoRMA(
        selectedEquipo.sn,
        `REINGRESO: ${reingresoData.observaciones}`
      );

      setSuccess(true);
      setReingresoOpen(false);
      cargarEquiposRMA();

      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al registrar reingreso');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ NUEVO: Registrar baja definitiva
  const registrarBaja = async () => {
    if (!bajaData.confirmacion) {
      setError('Debes marcar la casilla de confirmación para proceder');
      setTimeout(() => setError(''), 4000);
      return;
    }

    if (!bajaData.motivo.trim()) {
      setError('Debes indicar el motivo de la baja definitiva');
      setTimeout(() => setError(''), 4000);
      return;
    }

    setSubmitting(true);
    try {
      await warehouseAPI.darDeBajaDefinitiva(
        selectedEquipo.sn,
        bajaData.motivo
      );

      setSuccess(true);
      setBajaOpen(false);
      cargarEquiposRMA();

      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al dar de baja definitiva');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const NOMBRES_FALLA = {
    FISICO: 'Daño Físico',
    ELECTRONICO: 'Falla Electrónica',
    FIRMWARE: 'Falla de Firmware',
    CONEXION: 'Falla de Conexión',
    OTRO: 'Otra Falla'
  };

  // Usa el tipo de falla registrado; en equipos antiguos sin tipo lo deduce del texto del diagnóstico
  const categoriaFalla = (equipo) => {
    const diag = equipo.diagnostico_rma || '';
    if (diag.startsWith('[BAJA DEFINITIVA]')) return 'BAJA';
    if (equipo.tipo_falla) return equipo.tipo_falla;
    const t = diag.toLowerCase();
    if (!t) return 'SIN_DATO';
    if (t.includes('físico') || t.includes('fisico')) return 'FISICO';
    if (t.includes('electrónico') || t.includes('electronico')) return 'ELECTRONICO';
    if (t.includes('firmware')) return 'FIRMWARE';
    if (t.includes('conexión') || t.includes('conexion')) return 'CONEXION';
    return 'OTRO';
  };

  const getTipoFalla = (equipo) => {
    const c = categoriaFalla(equipo);
    if (c === 'BAJA') return 'Baja Definitiva';
    if (c === 'SIN_DATO') return 'No especificado';
    return NOMBRES_FALLA[c] || 'Falla Técnica';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // ✅ NUEVO: Saber si un equipo ya está dado de baja definitiva
  const esBajaDefinitiva = (equipo) => {
    return equipo.diagnostico_rma && equipo.diagnostico_rma.startsWith('[BAJA DEFINITIVA]');
  };

  const filteredEquipos = equiposRMA.filter(e => {
    const c = categoriaFalla(e);
    if (tabValue === 0) return true;
    if (tabValue === 1) return c === 'FISICO';
    if (tabValue === 2) return c === 'ELECTRONICO';
    if (tabValue === 3) return c === 'FIRMWARE' || c === 'CONEXION';
    if (tabValue === 4) return !['FISICO', 'ELECTRONICO', 'FIRMWARE', 'CONEXION'].includes(c);
    return true;
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          RMA / Bajas Técnicas
        </Typography>
        <Button
          variant="outlined"
          onClick={cargarEquiposRMA}
          startIcon={<Refresh />}
          disabled={loading}
        >
          Actualizar
        </Button>
      </Box>

      <ActasRMA />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee' }}>
                <Typography variant="h4" color="error">
                  {equiposRMA.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Equipos en RMA
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
                <Typography variant="h4" color="warning.main">
                  {equiposRMA.filter(e => categoriaFalla(e) === 'FISICO').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Daño Físico
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8eaf6' }}>
                <Typography variant="h4" color="info.main">
                  {equiposRMA.filter(e => categoriaFalla(e) === 'ELECTRONICO').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Falla Electrónica
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#424242' }}>
                <Typography variant="h4" sx={{ color: '#fff' }}>
                  {equiposRMA.filter(e => esBajaDefinitiva(e)).length}
                </Typography>
                <Typography variant="body2" sx={{ color: '#fff' }}>
                  Baja Definitiva
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
            <Tab label="Todos" />
            <Tab label="Daño Físico" />
            <Tab label="Falla Electrónica" />
            <Tab label="Firmware / Conexión" />
            <Tab label="Otros" />
          </Tabs>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>SN</TableCell>
                  <TableCell>Modelo</TableCell>
                  <TableCell>Tipo de Falla</TableCell>
                  <TableCell>Fecha RMA</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Cargando equipos en RMA...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredEquipos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                      <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                      <Typography variant="body1" color="success.main">
                        No hay equipos en RMA
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Todos los equipos están operativos o en buen estado
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEquipos.map((equipo) => {
                    const esBaja = esBajaDefinitiva(equipo);
                    return (
                      <TableRow
                        key={equipo.id}
                        hover
                        sx={esBaja ? { bgcolor: '#fafafa', opacity: 0.85 } : {}}
                      >
                        <TableCell>
                          <strong>{equipo.sn}</strong>
                        </TableCell>
                        <TableCell>{equipo.modelo}</TableCell>
                        <TableCell>
                          <Chip
                            label={getTipoFalla(equipo)}
                            size="small"
                            color={esBaja ? 'default' : (categoriaFalla(equipo) === 'FISICO' ? 'error' : 'warning')}
                            icon={esBaja ? <DangerousIcon /> : <Build />}
                          />
                        </TableCell>
                        <TableCell>{formatDate(equipo.fecha_rma)}</TableCell>
                        <TableCell>
                          <Chip
                            label={esBaja ? "Baja Definitiva" : (equipo.fecha_rma ? "En Proceso" : "Pendiente")}
                            color={esBaja ? "default" : (equipo.fecha_rma ? "info" : "default")}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Ver detalle">
                            <IconButton size="small" onClick={() => verDetalle(equipo)}>
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          {!esBaja && (
                            <>
                              <Tooltip title="Reingresar a inventario (reparado)">
                                <IconButton size="small" color="success" onClick={() => abrirReingreso(equipo)}>
                                  <AssignmentReturn />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Dar de baja definitiva (irrecuperable)">
                                <IconButton size="small" color="error" onClick={() => abrirBaja(equipo)}>
                                  <DangerousIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog de Detalle */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Detalle de RMA - {selectedEquipo?.sn}
        </DialogTitle>
        <DialogContent>
          {selectedEquipo && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Modelo:</Typography>
                  <Typography variant="body1">{selectedEquipo.modelo}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Diagnóstico:</Typography>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5', mt: 0.5 }}>
                    <Typography variant="body2">
                      {selectedEquipo.diagnostico_rma || 'No especificado'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Fecha de ingreso a RMA:</Typography>
                  <Typography variant="body1">{formatDate(selectedEquipo.fecha_rma)}</Typography>
                </Grid>
                {selectedEquipo.observaciones && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Observaciones adicionales:</Typography>
                    <Typography variant="body2">{selectedEquipo.observaciones}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Cerrar</Button>
          {selectedEquipo && !esBajaDefinitiva(selectedEquipo) && (
            <>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DangerousIcon />}
                onClick={() => {
                  setDetailOpen(false);
                  abrirBaja(selectedEquipo);
                }}
              >
                Dar de Baja Definitiva
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  setDetailOpen(false);
                  abrirReingreso(selectedEquipo);
                }}
              >
                Reingresar a Inventario
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog de Reingreso */}
      <Dialog open={reingresoOpen} onClose={() => !submitting && setReingresoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Reingreso a Inventario
          {selectedEquipo && (
            <Typography variant="body2" color="text.secondary">
              SN: {selectedEquipo.sn} - {selectedEquipo.modelo}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={reingresoData.reparado}
                  onChange={(e) => setReingresoData({ ...reingresoData, reparado: e.target.checked })}
                />
              }
              label="¿El equipo fue reparado correctamente?"
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Observaciones del reingreso"
              value={reingresoData.observaciones}
              onChange={(e) => setReingresoData({ ...reingresoData, observaciones: e.target.value })}
              placeholder="Indique las reparaciones realizadas, pruebas realizadas, etc."
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReingresoOpen(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={registrarReingreso}
            variant="contained"
            color="success"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Reingresar a Inventario'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ NUEVO: Dialog de Baja Definitiva */}
      <Dialog open={bajaOpen} onClose={() => !submitting && setBajaOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#ffebee', color: '#c62828' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DangerousIcon />
            Dar de Baja Definitiva
          </Box>
          {selectedEquipo && (
            <Typography variant="body2" color="text.secondary">
              SN: {selectedEquipo.sn} - {selectedEquipo.modelo}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              ⚠️ Esta acción marcará el equipo como <strong>irrecuperable</strong>.
              Solo úsala cuando el equipo no valga la pena reparar.
              El equipo quedará registrado como baja definitiva en el sistema.
            </Alert>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Motivo de la baja definitiva *"
              value={bajaData.motivo}
              onChange={(e) => setBajaData({ ...bajaData, motivo: e.target.value })}
              placeholder="Ej: Placa quemada sin reparación posible, daño por agua severo, componentes obsoletos sin repuesto, etc."
              required
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={bajaData.confirmacion}
                  onChange={(e) => setBajaData({ ...bajaData, confirmacion: e.target.checked })}
                  color="error"
                />
              }
              label={
                <Typography variant="body2">
                  Confirmo que este equipo <strong>NO es reparable</strong> y debe ser dado de baja
                </Typography>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBajaOpen(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={registrarBaja}
            variant="contained"
            color="error"
            disabled={submitting || !bajaData.confirmacion || !bajaData.motivo.trim()}
            startIcon={submitting ? <CircularProgress size={20} /> : <DangerousIcon />}
          >
            {submitting ? 'Procesando...' : 'Confirmar Baja Definitiva'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de éxito */}
      {success && (
        <Alert
          severity="success"
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}
          onClose={() => setSuccess(false)}
        >
          Operación realizada exitosamente
        </Alert>
      )}

      {/* Snackbar de error */}
      {error && (
        <Alert
          severity="error"
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default RMA;
